import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { FiPlusCircle, FiEdit2, FiTrash2, FiLayers } from "react-icons/fi";
import feeStructureService from "../../../services/feeStructureService";

export default function FeeStructureList() {
    const navigate = useNavigate();
    const [structures, setStructures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchStructures = async () => {
        setLoading(true);
        try {
            const res = await feeStructureService.getAll();
            setStructures(res.data || []);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchStructures();
    }, []);

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        try {
            await feeStructureService.remove(deleteTarget._id);
            toast.success("Fee structure deleted.");
            setDeleteTarget(null);
            fetchStructures();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setDeleting(false);
        }
    };

    const totalFor = (structure) =>
        (structure.heads || []).reduce((sum, h) => sum + (h.defaultAmount || 0), 0);

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Fee Structures</h1>
                    <p className="page-subtitle">
                        Define fee heads per course and year. Saving here auto-updates matching students' decided fees.
                    </p>
                </div>
                <button className="btn btn-primary" onClick={() => navigate("/settings/fee-structures/add")}>
                    <FiPlusCircle size={14} style={{ marginRight: 6 }} />
                    Add Fee Structure
                </button>
            </div>

            <div className="card">
                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading fee structures...
                        </div>
                    ) : structures.length === 0 ? (
                        <div className="empty-state">
                            <FiLayers />
                            <p>No fee structures defined yet. Add one to get started.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Academic Year</th>
                                        <th>Course</th>
                                        <th>Course Year</th>
                                        <th>Heads</th>
                                        <th>Default Total</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {structures.map((s) => (
                                        <tr key={s._id}>
                                            <td>{s.academicYear?.label || "—"}</td>
                                            <td>{s.course?.shortName || s.course?.courseName || "Course"}</td>
                                            <td>{s.yearNumber ? `${s.yearNumber}${s.yearNumber === 1 ? "st" : s.yearNumber === 2 ? "nd" : s.yearNumber === 3 ? "rd" : "th"} Year` : "—"}</td>
                                            <td>
                                                <span className="badge badge-blue">{(s.heads || []).length} heads</span>
                                            </td>
                                            <td style={{ fontWeight: 600 }}>₹{totalFor(s).toLocaleString("en-IN")}</td>
                                            <td>
                                                <div style={{ display: "flex", gap: "0.4rem", justifyContent: "flex-end" }}>
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        title="Edit"
                                                        onClick={() => navigate(`/settings/fee-structures/${s._id}/edit`)}
                                                    >
                                                        <FiEdit2 size={13} />
                                                    </button>
                                                    <button className="btn btn-outline btn-sm" title="Delete" onClick={() => setDeleteTarget(s)}>
                                                        <FiTrash2 size={13} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>

            {deleteTarget && (
                <div className="modal-overlay" onClick={() => !deleting && setDeleteTarget(null)}>
                    <div className="modal" onClick={(e) => e.stopPropagation()}>
                        <h3>Delete Fee Structure</h3>
                        <p>
                            Are you sure you want to delete the structure for <strong>{deleteTarget.course?.shortName || "Course"} — Year {deleteTarget.yearNumber}</strong>?
                            This won't change fees already saved on students, only removes the structure itself.
                        </p>
                        <div className="modal-actions">
                            <button className="btn btn-outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                                Cancel
                            </button>
                            <button className="btn btn-danger" onClick={confirmDelete} disabled={deleting}>
                                {deleting ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}